import { ObligationType as Obligation_Type_Enum } from '@risksmart-app/domain/src/types/consts/obligation-type';

/**
 * The drill-down order, parent → child. Selecting in one column reveals the next
 * and clears every column to its right.
 */
export const typeOrder = [
  Obligation_Type_Enum.Standard,
  Obligation_Type_Enum.Chapter,
  Obligation_Type_Enum.Rule,
  Obligation_Type_Enum.Task,
] as const;

/**
 * How deep each type sits in {@link typeOrder}.
 *
 * A `Record` keyed by the enum rather than an `indexOf`: a lookup cannot miss, and
 * adding a value to `Obligation_Type_Enum` becomes a compile error here instead of a
 * silent `-1` that would make `applySelection` keep the wrong ancestors.
 * `selection.test.ts` asserts it stays in step with `typeOrder`.
 */
const depthByType: Record<Obligation_Type_Enum, number> = {
  [Obligation_Type_Enum.Standard]: 0,
  [Obligation_Type_Enum.Chapter]: 1,
  [Obligation_Type_Enum.Rule]: 2,
  [Obligation_Type_Enum.Task]: 3,
};

/**
 * Which obligation is selected in each column. One source of truth for both the
 * card highlight and the child column's contents — the columns hold no selection
 * state of their own, so a selection can never survive the removal of the row it
 * points at (the same role `DashboardState` plays in the Risk Dashboard).
 */
export type DashboardSelection = Partial<Record<Obligation_Type_Enum, string>>;

/**
 * Applies a selection, keeping the columns to the left and clearing every column
 * to the right. Clearing is what stops a descendant staying highlighted under an
 * ancestor it no longer belongs to.
 */
export const applySelection = (
  previous: DashboardSelection,
  type: Obligation_Type_Enum,
  obligationId: string
): DashboardSelection => {
  const depth = depthByType[type];

  return typeOrder.slice(0, depth).reduce<DashboardSelection>(
    (kept, ancestorType) => ({
      ...kept,
      [ancestorType]: previous[ancestorType],
    }),
    { [type]: obligationId }
  );
};
